import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { publicAPI, ssoAPI } from '../api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowRight, AlertCircle, Key, Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [deployment, setDeployment] = useState({ is_on_premise: false, sso_enabled: false, sso_provider: '' });
  const { login, loginFromToken } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    document.title = 'Login | BrainShift AI Workspace';
    publicAPI.getDeploymentInfo().then(r => setDeployment(r.data || {})).catch(() => {});
    // Capture SSO callback token from URL hash (#sso_token=...)
    if (window.location.hash.startsWith('#sso_token=')) {
      const t = window.location.hash.slice('#sso_token='.length);
      if (t && loginFromToken) {
        loginFromToken(t).then(u => {
          window.history.replaceState(null, '', '/login');
          navigate(u?.is_super_admin ? '/superadmin' : '/dashboard');
        }).catch(() => setError('SSO login failed — invalid token'));
      }
    }
  }, [loginFromToken, navigate]);

  const handleSSO = async () => {
    setError('');
    try {
      const r = await ssoAPI.initiate();
      window.location.href = r.data.authorization_url;
    } catch (err) {
      setError(err.response?.data?.detail || 'SSO is not available');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      navigate(data.user?.is_super_admin ? '/superadmin' : '/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-[#F0F4FA]">
        <img
          src="/brainshift-bg.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <div className="flex items-center gap-3 mb-6">
            <img src="/brainshift-logo.png" alt="BrainShift" className="w-auto" style={{ height: '4.75rem' }} />
          </div>
          <p className="text-[#374151] text-lg max-w-md leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
            Enterprise-grade knowledge workspace with AI-powered compliance analysis, conflict detection, and intelligent document insights.
          </p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-2 lg:hidden">
            <img src="/brainshift-logo.png" alt="BrainShift" className="w-auto" style={{ height: '4.75rem' }} />
          </div>
          <h1
            className="text-3xl md:text-4xl font-bold tracking-tight text-[#111827] mb-2"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            Sign in
          </h1>
          <p className="text-[#4B5563] mb-8" style={{ fontFamily: 'Inter, sans-serif' }}>
            Access your knowledge workspace
          </p>

          {error && (
            <div className="flex items-center gap-2 p-3 mb-6 border border-[#C8102E]/30 bg-[#C8102E]/5 text-[#C8102E] text-sm rounded-xl" data-testid="login-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" autoComplete="on">
            <div>
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-2 block">
                Email
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                className="rounded-xl border-[#E5E7EB] focus:ring-2 focus:ring-[#6590FF] focus:ring-offset-0 h-11"
                placeholder="you@company.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-2 block">
                Password
              </Label>
              {/*
                Security: input stays type="password" at all times by default.  Toggling to
                type="text" only happens on an explicit user click of the eye button.  We never
                programmatically flip the type based on focus/hover or any other event, which
                prevents shoulder-surfers from seeing the password unless the user opts in.
                `autoComplete="current-password"` enables secure browser autofill on every
                modern engine (Chrome, Edge, Safari, Firefox) and pairs with the `name="email"`
                + `autoComplete="username"` above so credential managers can save logins.
                `spellCheck=false` + `autoCorrect=off` + `autoCapitalize=none` stop the OS from
                exposing the password through suggestion bars.  `data-1p-ignore` is honoured by
                older 1Password versions, harmless elsewhere.
              */}
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  className="rounded-xl border-[#E5E7EB] focus:ring-2 focus:ring-[#6590FF] focus:ring-offset-0 h-11 pr-11"
                  placeholder="Enter your password"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  tabIndex={-1}
                  className="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-[#9CA3AF] hover:text-[#4B5563] focus:outline-none focus-visible:text-[#6590FF] rounded-r-xl transition-colors"
                  data-testid="login-password-toggle"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#6590FF] hover:bg-[#4B7CFF] text-white font-semibold text-sm flex items-center justify-center gap-2 rounded-xl transition-colors disabled:opacity-50"
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>

          {deployment.is_on_premise && deployment.sso_enabled && (
            <>
              <div className="flex items-center gap-2 my-5">
                <div className="flex-1 h-px bg-[#E5E7EB]" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#9CA3AF]">or</span>
                <div className="flex-1 h-px bg-[#E5E7EB]" />
              </div>
              <button onClick={handleSSO} type="button"
                className="w-full h-11 border-2 border-[#6590FF] text-[#6590FF] hover:bg-[#6590FF]/5 font-semibold text-sm flex items-center justify-center gap-2 rounded-xl transition-colors"
                data-testid="sso-login-btn">
                <Key className="w-4 h-4" /> Sign in with SSO ({deployment.sso_provider || 'OIDC'})
              </button>
              <p className="text-[10px] text-[#6B7280] text-center mt-2">Staff users authenticate via your organization's identity provider.</p>
            </>
          )}

          {!deployment.is_on_premise && (
            <p className="mt-6 text-sm text-[#4B5563] text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
              Don't have an account?{' '}
              <Link to="/register" className="text-[#6590FF] font-semibold hover:underline" data-testid="register-link">
                Create one
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
